import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { withTheme } from "styled-components";
import SmartTimePicker from "../TimePicker/TimePicker";
import DateSelect from "../../../ui-library/DateAndTime/Date/DateSelect";

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const TimePickersWrapper = styled.div`
  display: flex;
  flex-direction: row;
  @media (max-width: 413px) {
    flex-direction: column;
    width: 100%;

    .start-time-wrapper {
      margin-right: 0 !important;

      .date-picker-wrapper {
        width: 100%;
      }
    }

    .end-time-wrapper {
      margin-top: 10px;
    }
  }
`;

const DateStartEndTime = (props) => {
  const {
    validate,
    onValidate,
    onChange,
    readOnly,
    defaultStartTime,
    defaultEndTime,
    defaultDate,
    onError,
    timeIntervals = 1,
    maxDate,
    minDate,
    timeFormat,
  } = props;

  const { t } = useTranslation();

  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const [maxTime, setMaxTime] = useState(null);

  const [showErrorDate, setShowErrorDate] = useState(false);
  const [isValidDate, setIsValidDate] = useState(true);

  const validateStartEndTime = useCallback(() => {
    const hasErrors = startTime === "" || endTime === "";

    onValidate(!hasErrors, {
      startTime: startTime,
      endTime: endTime,
    });
  }, [startTime, onValidate, endTime]);

  useEffect(() => {
    if (!date || moment(date).startOf("day").isSame(moment().startOf("day"))) {
      setMaxTime(moment().toDate());
    } else {
      setMaxTime(moment(date).endOf("day").toDate());
    }
  }, [date]);

  useEffect(() => {
    setDate(defaultDate);
  }, [defaultDate]);

  useEffect(() => {
    setStartTime(defaultStartTime);
  }, [defaultStartTime]);

  useEffect(() => {
    setEndTime(defaultEndTime);
  }, [defaultEndTime]);

  useEffect(() => {
    if (validate) {
      validateStartEndTime();
    }
  }, [validate, validateStartEndTime]);

  const onChangeDate = (date) => {
    onChange(true, {
      date: date,
      startTime: startTime,
      endTime: endTime,
    });
  };

  const handleStartTimeChange = (value) => {
    const currentDay = moment(date).get("date");
    const newDay = moment(value).set("date", currentDay).toDate();

    setStartTime(newDay);

    onChange(true, {
      date: date,
      startTime: newDay,
      endTime: endTime,
    });
  };

  const handleEndTimeChange = (value) => {
    const currentDay = moment(date).get("date");
    const newDay = moment(value).set("date", currentDay);

    setEndTime(newDay.toDate());

    onChange(true, {
      date: date,
      startTime: startTime,
      endTime: newDay.toDate(),
    });
  };

  const errorHandling = {
    showErrorDate: showErrorDate,
    setShowErrorDate: setShowErrorDate,
    isValidDate: isValidDate,
    setIsValidDate: setIsValidDate,
  };

  return (
    <StyledWrapper data-testid="date-start-end-time">
      <div className="date-wrapper mb-4">
        <DateSelect
          eventDate={date}
          maxDate={maxDate}
          minDate={minDate}
          setEventDate={setDate}
          errorHandling={errorHandling}
          onChange={onChangeDate}
          readOnly={readOnly}
        />
      </div>
      <TimePickersWrapper>
        <div className="start-time-wrapper me-4">
          <SmartTimePicker
            id="start-time-picker"
            data-testid="start-time-picker"
            onChange={handleStartTimeChange}
            value={startTime}
            readOnly={readOnly}
            maxTime={endTime || maxTime}
            title={t("appText.startTime")}
            onError={(error) => onError(error)}
            timeIntervals={timeIntervals}
            participantTimeFormat={timeFormat}
          />
        </div>
        <div className="end-time-wrapper">
          <SmartTimePicker
            id="end-time-picker"
            data-testid="end-time-picker"
            onChange={handleEndTimeChange}
            value={endTime}
            readOnly={readOnly}
            maxTime={maxTime}
            title={t("appText.endTime")}
            onError={(error) => onError(error)}
            timeIntervals={timeIntervals}
            participantTimeFormat={timeFormat}
          />
        </div>
      </TimePickersWrapper>
    </StyledWrapper>
  );
};

export default withTheme(DateStartEndTime);
